import { defineStore } from "pinia"
import { apiClient } from "@/services/api"
import { logger } from "@/services/logger"
import { mayRequestInBackground } from "@/services/backgroundRequests"
import { IS_VIEW_HELP_EMBED } from "@/services/viewHelp/embed"

const authHeaders = { headers: { tenant: true, authorization: true } }

/**
 * Pins per user and overview, as the backend allows (`FrontendPinService.MAX_PINS_PER_TYPE`). An
 * overview reads its pinned rows in one request of this size.
 */
export const MAX_PINS_PER_TYPE = 50

/** The pins request in flight, so callers that ask at the same time share it. */
let loading = null

/**
 * Per record (`type:id`), the last of the writes queued for it, and the state the server last confirmed
 * while they run. Writes to one record go out one after the other, so a quick second click cannot overtake
 * the first; when the last one has settled, the record shows what the server holds.
 */
const writes = new Map()
const confirmed = new Map()

/**
 * Bumped by every reset. An answer to a request sent before a sign-out belongs to the person who left, and
 * must not become the pins of whoever signs in next.
 */
let session = 0

/**
 * The records the signed-in user has pinned in the overviews, per kind (`PROJECT`, `CUSTOMER`, `USER`, …,
 * the backend's `EntityType` names), as ids. The rows themselves are read by each overview through its own
 * list endpoint with `pinned=true`; see docs/overview-lists.md ("Angepinnte Zeilen").
 *
 * Read once per session and kept in step by the writes. Pins are the user's own and follow them to any
 * device, which is why they are stored on the server and not in `localStorage`.
 *
 * Inside the Erklärung's frame nothing is sent: the frame is a picture of the product and refuses writes,
 * so a pin set there lives in memory for the frame's short life (see docs/view-help.md).
 */
export const usePinStore = defineStore("pins", {
  state: () => ({
    /** `{ [type]: string[] }` */
    byType: {},
    loaded: false,
  }),
  getters: {
    isPinned: state => (type, id) => Boolean(type && id && state.byType[type]?.includes(id)),
    hasPins: state => type => (state.byType[type]?.length ?? 0) > 0,
  },
  actions: {
    /**
     * Loads the pins unless they are loaded; concurrent callers share one request. A failed load is asked
     * again by the next caller: the lists work without pins meanwhile, they only lose their pinned rows.
     *
     * Nobody presses anything for this read, so it follows the rule of the other unprompted requests
     * (services/backgroundRequests.js): not without a session, not for a locked-out workspace, not inside
     * the Erklärung's frame, whose pins start empty.
     */
    ensureLoaded() {
      if (this.loaded || !mayRequestInBackground()) return Promise.resolve()
      const own = session
      loading ??= apiClient
        .get("/pins", authHeaders)
        .then(res => {
          if (own !== session) return
          this.byType = res.data?.pins ?? {}
          this.loaded = true
        })
        .catch(error => logger.error(`Failed to load pins: ${error}`))
        .finally(() => {
          if (own === session) loading = null
        })
      return loading
    },

    /**
     * Pins or unpins one record, to the opposite of what its pin showed when it was pressed. Optimistic, so
     * the row moves at once; a refused write (the limit, a record deleted meanwhile) puts back what the
     * server holds, and the API client says why.
     */
    async toggle(type, id) {
      const target = !this.isPinned(type, id)
      // A load still in flight would otherwise answer over this change.
      await this.ensureLoaded()
      this.setPinned(type, id, target)
      if (IS_VIEW_HELP_EMBED) return

      const key = `${type}:${id}`
      const own = session
      if (!writes.has(key)) confirmed.set(key, !target)
      const write = (writes.get(key) ?? Promise.resolve())
        .then(() =>
          target
            ? apiClient.put(`/pins/${type}/${id}`, null, authHeaders)
            : apiClient.delete(`/pins/${type}/${id}`, authHeaders)
        )
        .then(
          () => confirmed.set(key, target),
          () => {}
        )
        .finally(() => {
          if (own !== session || writes.get(key) !== write) return
          this.setPinned(type, id, confirmed.get(key))
          writes.delete(key)
          confirmed.delete(key)
        })
      writes.set(key, write)
      return write
    },

    setPinned(type, id, pinned) {
      const ids = (this.byType[type] ?? []).filter(it => it !== id)
      this.byType = { ...this.byType, [type]: pinned ? [...ids, id] : ids }
    },

    /**
     * The pinned rows of one overview, read through its own list endpoint so they look like the other rows
     * and answer to the same rights, search and sort.
     *
     * No request when nothing of the kind is pinned. The answer is kept to the pinned ids here as well: a
     * list that does not know `pinned=true` (an older backend, the Erklärung's sample answers) answers with
     * every row, and those must not all land on top.
     *
     * @param params the overview's own list params (search, sort, reference narrowing)
     */
    async loadPinnedRows(type, path, params = {}) {
      await this.ensureLoaded()
      if (!this.hasPins(type)) return []
      const res = await apiClient.get(path, {
        ...authHeaders,
        params: { ...params, pinned: true, page: 0, itemsPerPage: MAX_PINS_PER_TYPE },
      })
      const rows = res.data?.content ?? []
      return rows.filter(row => this.isPinned(type, row.id))
    },

    reset() {
      session++
      loading = null
      writes.clear()
      confirmed.clear()
      this.byType = {}
      this.loaded = false
    },
  },
})

/**
 * The `pins` of an overview table that lists one kind from one endpoint.
 *
 * @param type the kind, as the backend names it (`PROJECT`, …)
 * @param path the overview's list endpoint (`/project`, …)
 * @param label names a row for the pin's accessible label
 * @param params the list params the overview adds on its own (its reference narrowing), read on each load
 */
export function overviewPins(type, path, { label, params = () => ({}) }) {
  return {
    typeOf: () => type,
    label,
    load: query => usePinStore().loadPinnedRows(type, path, { ...query, ...params() }),
  }
}
